import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";


import { BoardOverlay } from "./BoardOverlay";


/**
 * BoardMainSpace - A container component for the main board area that provides:
 * - Isolated development environment for charts and components
 * - Animation-ready structure with proper lifecycle management
 * - Editing and update capabilities
 * - Best practices for component organization
 * - Option to disable internal scrolling
 */
interface BoardMainSpaceProps {
  children?: ReactNode;
  className?: string;
  floatingAction?: ReactNode;
  disableScroll?: boolean;
  isEditMode?: boolean;
  fullPage?: boolean;
  isLoading?: boolean;
  isInitialLoading?: boolean;
  isTimeRangeExceeded?: boolean;
}

// Use forwardRef to expose the scrollable container
export const BoardMainSpace = React.forwardRef<HTMLDivElement, BoardMainSpaceProps>(({
  children,
  className = "",
  floatingAction,
  disableScroll = false,
  isEditMode = false,
  fullPage = false,
  isLoading = false,
  isInitialLoading = false,
  isTimeRangeExceeded = false,
}, ref) => {
  const { t } = useTranslation("common");


  return (
    <div
      id="onboarding-board-main"
      className={`
        board-main-space
        relative
        flex-1
        min-h-0
        bg-white dark:bg-neutral-900 rounded-lg border border-border-default dark:border-neutral-800
        ${fullPage ? '' : 'shadow-sm'}
        transition-all
        duration-300
        ease-in-out
        ${fullPage ? 'overflow-hidden' : ''}
        ${className}
      `}
    >
      {/* 
          Premium Unified Loading Overlay
          - Handles both initial loading and updates
          - Provides a consistent, blurred premium feel across the entire board
      */}
      <BoardOverlay 
        isVisible={isInitialLoading || isLoading || isTimeRangeExceeded} 
        message={isTimeRangeExceeded ? t("boards.timeRangeExceeded", "Time range exceeds plan limits. Please update your date range.") : undefined}
      />

      {/* Content Container with Animation Support */}
      <div
        ref={ref}
        onScroll={(e) => {
          const container = e.currentTarget;
          container.classList.add('scrolling');

          // Clear existing timeout
          const existingTimeout = container.dataset.scrollTimeout;
          if (existingTimeout) {
            clearTimeout(parseInt(existingTimeout));
          }

          // Set new timeout to remove class
          const timeoutId = setTimeout(() => {
            container.classList.remove('scrolling');
          }, 1000);

          container.dataset.scrollTimeout = timeoutId.toString();
        }}
        className={`
          h-full 
          overflow-x-hidden 
          transition-colors 
          duration-300 
          ease-in-out 
          scrollbar-hide
          ${fullPage ? "overflow-hidden" : "overflow-y-auto"}
          ${isEditMode ? "custom-scrollbar-edit" : ""}
          ${floatingAction ? (disableScroll ? "" : "pb-24") : ""}
        `}
      >
        {/* Main Content Area */}
        <div className="h-full">
          {children || (
            <div className="text-center py-12 text-text-tertiary">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-medium mb-2">
                {t("boardMainSpace.title")}
              </h3>
              <p className="text-sm">
                {t("boardMainSpace.description")}
                <br />
                {t("boardMainSpace.features")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Button */}
      {floatingAction && (
        <div className="absolute top-4 right-4 z-20">
          {floatingAction}
        </div>
      )}

      {/* Edit Mode Highlight Overlay - Positioned above scrollbar and content */}
      {isEditMode && (
        <div className="absolute inset-0 border-2 border-accent-primary/40 rounded-lg pointer-events-none z-[30] transition-all duration-300" />
      )}
    </div >
  );
});

export default BoardMainSpace;
